import { cn } from "../../utils";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
// Use relative imports to avoid circular dependency with @ac-uicore
import { MermaidDiagram } from "../mermaid-diagram/MermaidDiagram";
import { FC, ReactElement, ReactNode, memo } from "react";
import Plotly from "./Plotly";
import rehypeRaw from "rehype-raw";
import { TransparencyMarker, type TransparencyAnnotation } from "./TransparencyMarker";
import { ProvenanceBadge } from "./ProvenanceBadge";
import JSON5 from "json5";
import remarkDirective from "remark-directive";
import { visit } from "unist-util-visit";
import type { Node, Root } from "mdast";
import { getMarkdownTypographyClasses } from "../typography/typography-utils";
import remarkBreaks from "remark-breaks";
import "./styles.css";
import {
  AiCodeBlock as CodeBlock,
  CodeBlockActions,
  CodeBlockCopyButton,
  CodeBlockFilename,
  CodeBlockHeader,
  CodeBlockTitle,
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
} from "../../ai-elements";
import { ToolUIPart } from "ai";
import { nanoid } from "nanoid";
import { IconFile } from "@tabler/icons-react";

const getTextContent = (element: ReactElement): string => {
  if (typeof element.props.children === "string") {
    return element.props.children;
  }
  return "";
};

const remarkCustomDirectives = () => {
  return (tree: Root) => {
    visit(tree, node => {
      // Handle container directives (:::think)
      if (
        (node.type === "containerDirective" || node.type === "textDirective") &&
        node.name === "think"
      ) {
        const newNode = node as Node;
        newNode.type = "div";
        newNode.data = {
          hName: "think",
        };
      }
    });
  };
};

const ThinkComponent: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Reasoning>
      <ReasoningTrigger />
      <ReasoningContent>{children}</ReasoningContent>
    </Reasoning>
  );
};

const getToolCallData = (children?: string | ReactNode): ToolUIPart => {
  const defaultValue: ToolUIPart = {
    type: "tool-function",
    input: children,
    toolCallId: nanoid(),
    state: "output-available",
    output: "",
  };
  if (typeof children !== "string") {
    return defaultValue;
  }

  try {
    const data = JSON5.parse(children);
    const toolArgs = JSON5.parse(data.function.arguments);
    const toolCall: ToolUIPart = {
      type: data.type,
      toolCallId: nanoid(),
      state: "output-available",
      input: {
        function: data.function.name,
        arguments: toolArgs,
      },
      output: "",
      errorText: undefined,
    };
    return toolCall;
  } catch (error) {
    console.error("Failed to parse tool_call JSON:", error);
    return defaultValue;
  }
};

const ToolCallComponent: FC<{ children: ReactNode }> = ({ children }) => {
  const toolCall = getToolCallData(children);

  return (
    <Tool className="al-font-normal al-bg-white">
      <ToolHeader
        state={"output-available" as ToolUIPart["state"]}
        title="Fetching more context..."
        type={toolCall.type}
      />
      <ToolContent>
        <ToolInput input={toolCall.input} />
      </ToolContent>
    </Tool>
  );
};

interface AnnotationBlockData {
  label: string;
  annotation?: TransparencyAnnotation;
  annotationContent: string;
  isTableSource: boolean;
  isChartSource: boolean;
}

/** Extract the first non-empty line from markdown, stripping bold markers, for use as a badge label. */
function extractLabelFromContent(content: string): string {
  const firstLine = content.split("\n")[0].replace(/\*\*/g, "").trim();
  return firstLine || "Data Source";
}

/**
 * Parse an ```annotation code block element into structured annotation data.
 * Format: { label, data: { text, tier, content } }
 * Table/chart: { label: "table_N"/"chart_N", data: { content } }
 */
function getAnnotationBlockData(element: ReactElement): AnnotationBlockData | null {
  const text = getTextContent(element);
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    const label: string = parsed.label ?? "";
    const data = parsed.data ?? {};
    const annotationContent: string = data.content ?? "";
    const isTableSource = label.startsWith("table_");
    const isChartSource = label.startsWith("chart_");

    if (isTableSource || isChartSource) {
      return { label, annotationContent, isTableSource, isChartSource };
    }

    const annotation: TransparencyAnnotation = {
      text: data.text ?? "",
      tier: data.tier ?? "data_query",
    };

    return { label, annotation, annotationContent, isTableSource: false, isChartSource: false };
  } catch {
    return null;
  }
}

interface MarkdownProps {
  content: string;
  id?: string;
  components?: Components;
  className?: string;
  /**
   * AI-6339: provider context for citation URL construction. When omitted,
   * defaults to "snowflake" — preserving pre-AI-6339 behaviour for callers
   * that don't pass this prop.
   */
  provider?: CitationProvider;
}

export type CitationProvider = "snowflake" | "databricks";

/**
 * Generate URL for a citation entity type and ID, scoped by provider.
 *
 * AI-6339: Snowflake and Databricks tenants share entity-type names
 * (e.g. `warehouse`, `query`) but route to different pages. The provider
 * comes from the chat surface and defaults to `snowflake` to preserve the
 * pre-AI-6339 behaviour for any caller that hasn't been migrated.
 *
 * NOTE: this function duplicates the route map in
 * `apps/web/src/modules/playground/utils/citationParser.ts::getEntityUrl`.
 * The duplication exists because `lego` cannot import from `apps/web`.
 * A parity test in `apps/web/.../citationParser.parity.test.ts` pins the
 * two implementations together — update both when route paths change.
 * TODO: consolidate into `packages/ui-components` so both callers share
 * a single source of truth.
 *
 * Exported so the parity test can import it; not part of the lego public API.
 */
export function getCitationUrl(provider: CitationProvider, type: string, id: string): string {
  if (provider === "databricks") {
    switch (type) {
      case "warehouse":
        return `/sql-warehouses/${encodeURIComponent(id)}`;
      case "query":
        return `/queries/${encodeURIComponent(id)}`;
      case "cluster":
        return `/clusters/${encodeURIComponent(id)}`;
      case "job":
        return `/jobs/${encodeURIComponent(id)}`;
      default:
        return "#";
    }
  }
  // snowflake (default)
  switch (type) {
    case "warehouse":
      return `/warehouse/${id}`;
    case "table":
      return /^\d+$/.test(id) ? `/datasets/${id}` : `/datasets?table=${encodeURIComponent(id)}`;
    case "query":
      return `/query/${encodeURIComponent(id)}`;
    case "user":
      return `/users?selectedUser=${encodeURIComponent(id)}`;
    case "workload":
      return `/query/workloads/task-details/${encodeURIComponent(id)}`;
    // Tableau citation entity types (kept in sync with
    // apps/web/src/modules/playground/utils/citationParser.ts —
    // there's a parity test that fails loudly if the two diverge).
    // The backend `TableauCitationResolver` rewrites the model-emitted
    // LUID/email into a URL-encoded full rk before the FE sees it, so
    // the id is already encoded for direct use in a single path segment.
    case "tableau_dashboard":
      return `/bi-reports/${id}`;
    case "tableau_user":
      return `/bi-reports/users/${id}`;
    case "tableau_datasource":
      return `/bi-reports/dashboards`;
    case "tableau_dashboard_group":
      return `/bi-reports/dashboards`;
    case "tableau_job":
      return `/query/tableau-jobs/${id}`;
    default:
      return "#";
  }
}

// processCitations, processAnnotations, and escapeHtml live in sibling files so
// they can be unit-tested without pulling in `react-markdown` (ESM-only — Jest
// needs extra transform config to import this file directly).
import { processCitations } from "./processCitations";
import { processAnnotations } from "./processAnnotations";

/** Memoized to prevent unnecessary re-renders when only sibling props change. */
const MarkdownInner: FC<MarkdownProps> = ({
  content,
  id,
  components,
  className,
  provider = "snowflake",
}) => {
  // Convert <think> tags to :::think directives, then process annotation codeblocks.
  const withDirectives = content
    .replace(/<think>/g, "\n:::think\n")
    .replace(/<\/think>/g, "\n:::\n");
  const transformedContent = processCitations(processAnnotations(withDirectives));

  const typoClasses = getMarkdownTypographyClasses();

  return (
    <div className={cn("al-prose dark:al-prose-invert al-max-w-none react-markdown", className)}>
      <ReactMarkdown
        remarkPlugins={[
          [remarkGfm, { singleTilde: false }],
          remarkDirective,
          remarkCustomDirectives,
          remarkBreaks,
        ]}
        rehypePlugins={[rehypeRaw]}
        className="[&>*:not(:last-child)]:al-mb-4 [&>*:last-child]:al-mb-0"
        components={{
          h1: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/heading-has-content
            <h1 className={cn(typoClasses.h1, className)} {...props} />
          ),
          h2: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/heading-has-content
            <h2 className={cn(typoClasses.h2, className)} {...props} />
          ),
          h3: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/heading-has-content
            <h3 className={cn(typoClasses.h3, className)} {...props} />
          ),
          h4: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/heading-has-content
            <h4 className={cn(typoClasses.h4, className)} {...props} />
          ),
          h5: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/heading-has-content
            <h5 className={cn(typoClasses.h5, className)} {...props} />
          ),
          h6: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/heading-has-content
            <h6 className={cn(typoClasses.h6, className)} {...props} />
          ),
          p: ({ className, ...props }) => (
            <p className={cn(typoClasses.body, className)} {...props} />
          ),
          a: ({ className, ...props }) => (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a className={cn(typoClasses.link, className)} {...props} />
          ),
          ul: ({ className, ...props }) => (
            <ul className={cn(typoClasses.list, className)} {...props} />
          ),
          ol: ({ className, ...props }) => (
            <ol className={cn(typoClasses.orderedList, className)} {...props} />
          ),
          blockquote: ({ className, ...props }) => (
            <blockquote className={cn(typoClasses.blockquote, className)} {...props} />
          ),
          span: ({ className, children, ...props }) => {
            if (className === "citation-link") {
              const citationType = (props as { "data-citation-type"?: string })[
                "data-citation-type"
              ];
              const citationId = (props as { "data-citation-id"?: string })["data-citation-id"];
              if (citationType && citationId) {
                const url = getCitationUrl(provider, citationType, citationId);
                // `processCitations` is provider-agnostic and wraps every
                // [[type:id:label]] marker in a citation-link span, so we may
                // get here with a type that isn't valid for the active
                // provider (e.g. `table` on Databricks). In that case
                // getCitationUrl returns "#" — render the label as plain
                // text instead of a broken anchor that scrolls to top.
                if (url === "#") {
                  return <>{children}</>;
                }
                return (
                  <a
                    href={url}
                    onClick={e => {
                      // SPA-style navigation via history.pushState so the
                      // browser doesn't do a full page reload. A full reload
                      // would send the URL to the dev server which decodes
                      // `%2F` to `/` in path segments, corrupting Tableau
                      // entity citation URLs like
                      // `/bi-reports/users/tableau_user%3A5%3A%2F%2Fsite.luid`
                      // — the dev server interprets the decoded `://` as
                      // extra path separators and the route fails to match.
                      // history.pushState preserves percent-encoded chars
                      // and lets React Router handle the route resolution
                      // client-side (same as `<Link>` would).
                      if (
                        e.defaultPrevented ||
                        e.button !== 0 ||
                        e.metaKey ||
                        e.ctrlKey ||
                        e.shiftKey ||
                        e.altKey
                      ) {
                        return;
                      }
                      e.preventDefault();
                      window.history.pushState({}, "", url);
                      window.dispatchEvent(new PopStateEvent("popstate"));
                    }}
                    className="al-inline-flex al-items-center al-gap-1 al-px-1.5 al-py-0.5 al-bg-blue-50 al-text-blue-700 al-rounded al-text-sm al-font-medium al-no-underline hover:al-bg-blue-100 al-transition-colors"
                  >
                    {children}
                  </a>
                );
              }
            }
            if (className === "annotation-trigger" || className === "annotation-inline") {
              const annotationStr = (props as { "data-annotation"?: string })["data-annotation"];
              if (annotationStr) {
                try {
                  const parsed = JSON.parse(annotationStr);
                  const data = parsed.data ?? {};
                  const annotation: TransparencyAnnotation = {
                    text: data.text ?? "",
                    tier: data.tier ?? "data_query",
                  };
                  const content: string = data.content ?? "";
                  return (
                    <TransparencyMarker
                      annotation={annotation}
                      popoverContent={<Markdown content={content} />}
                      trigger={className === "annotation-trigger" ? children : undefined}
                    />
                  );
                } catch {
                  return null;
                }
              }
            }
            return (
              <span className={className} {...props}>
                {children}
              </span>
            );
          },
          table: ({ className, ...props }) => (
            <div className="table-wrapper">
              <table className={className} {...props} />
            </div>
          ),
          pre: ({ node: _node, className, children, ...props }) => {
            if (children && typeof children === "object" && "props" in children) {
              const childClassName = (children as ReactElement).props?.className;

              if (childClassName === "language-annotation") {
                const parsed = getAnnotationBlockData(children as ReactElement);
                if (!parsed) return null;

                if (parsed.isTableSource || parsed.isChartSource) {
                  return (
                    <ProvenanceBadge
                      variant={parsed.isTableSource ? "table" : "chart"}
                      popoverContent={
                        parsed.annotationContent ? (
                          <Markdown content={parsed.annotationContent} />
                        ) : undefined
                      }
                      badgeLabel={extractLabelFromContent(parsed.annotationContent)}
                    />
                  );
                }

                return null;
              }

              if (childClassName === "language-plotly") {
                try {
                  const text = getTextContent(children as ReactElement);
                  const jsonData = JSON5.parse(text);
                  return <Plotly jsonData={jsonData} />;
                } catch (error) {
                  console.error("Failed to parse Plotly JSON:", error);
                }
              }
            }

            return (
              <pre {...props} className={cn(typoClasses.code, className)}>
                {children}
              </pre>
            );
          },
          // @ts-expect-error valid
          think: ThinkComponent,
          code({ className, children, node }) {
            const nodeChild = node?.children?.[0] as { type?: string; value?: string } | undefined;
            const text = nodeChild?.value || "";
            const codeblock = text.includes("\n");

            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";

            if (!codeblock) {
              return (
                <code className="al-px-1.5 al-py-0.5 al-rounded-md al-bg-gray-100 dark:al-bg-gray-800 al-text-sm">
                  {children}
                </code>
              );
            }

            if (language === "mermaid") {
              return (
                <MermaidDiagram
                  content={String(children).replace(/\n$/, "")}
                  id={id || crypto.randomUUID()}
                />
              );
            }

            if (language === "tool_call") {
              return <ToolCallComponent>{children}</ToolCallComponent>;
            }

            return (
              <CodeBlock
                code={String(children).replace(/\n$/, "")}
                language={language as "sql" | "yaml" | "markdown" | "json" | "javascript"}
                contentClassName="al-max-h-[300px]"
              >
                <CodeBlockHeader>
                  <CodeBlockTitle>
                    <IconFile size={14} />
                    <CodeBlockFilename>{language.toUpperCase()}</CodeBlockFilename>
                  </CodeBlockTitle>
                  <CodeBlockActions>
                    <CodeBlockCopyButton
                      onCopy={() => console.log("Copied code to clipboard")}
                      onError={() => console.error("Failed to copy code to clipboard")}
                    />
                  </CodeBlockActions>
                </CodeBlockHeader>
              </CodeBlock>
            );
          },
          ...components,
        }}
      >
        {transformedContent || ""}
      </ReactMarkdown>
    </div>
  );
};

export const Markdown = memo(MarkdownInner);
